import { getDeerReviewResultRoute } from 'Deer/constants/nonDeerRoutes';
import { useSidebarNavMatchRoute } from 'components/hooks/useSidebarNavMatchRoute';
import { useSelector } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { valuesFromSlack } from 'screens/admin/OrgSettings/timezone/values';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { selectOrgData } from 'selectors/orgData';

import { REVIEW_FORM_COMPLETION_STATUS, REVIEW_SUBMISSION_STATUS_MAP } from 'constants/ProjectConstants';

import { REVIEW_STATUS_VALUES } from './manageAndInsights/ReviewManage/components/ReviewManageTable/components/ShareResultsDrawer/PartialPublishContext';

const ANSWER_TYPE_REQUIRED_FILLED_VALIDATOR = {
  DROP_DOWN: ({
    isRequired,
    selectedAnswerOptions,
    enableFeedbackText,
    isTextRequired,
    feedbackText,
    feedbackTextHtml,
  }) => {
    if (isRequired && selectedAnswerOptions?.length === 0) {
      return false;
    }
    if (enableFeedbackText && isTextRequired && (!feedbackText || !feedbackTextHtml)) {
      return false;
    }
    return true;
  },
  MULTI_SELECT: ({
    isRequired,
    selectedAnswerOptions,
    enableFeedbackText,
    isTextRequired,
    feedbackText,
    feedbackTextHtml,
    minOptions,
    maxOptions,
    answerOptions = [],
  }) => {
    const min = minOptions ?? 0;
    const max = maxOptions ?? answerOptions.length;

    if (isRequired && (selectedAnswerOptions?.length < min || selectedAnswerOptions.length > max)) {
      return false;
    }
    if (enableFeedbackText && isTextRequired && (!feedbackText || !feedbackTextHtml)) {
      return false;
    }
    return true;
  },

  NONE: ({ enableFeedbackText, isTextRequired, feedbackText, feedbackTextHtml }) => {
    if (enableFeedbackText && isTextRequired && (!feedbackText || !feedbackTextHtml)) {
      return false;
    }
    return true;
  },
  STAR_RATING: ({
    isRequired,
    selectedAnswerOptions,
    enableFeedbackText,
    isTextRequired,
    feedbackText,
    feedbackTextHtml,
  }) => {
    if (isRequired && selectedAnswerOptions?.length === 0) {
      return false;
    }
    if (enableFeedbackText && isTextRequired && (!feedbackText || !feedbackTextHtml)) {
      return false;
    }
    return true;
  },
  NUMBER_RATING: ({
    isRequired,
    selectedAnswerOptions,
    enableFeedbackText,
    isTextRequired,
    feedbackText,
    feedbackTextHtml,
  }) => {
    if (isRequired && selectedAnswerOptions?.length === 0) {
      return false;
    }
    if (enableFeedbackText && isTextRequired && (!feedbackText || !feedbackTextHtml)) {
      return false;
    }
    return true;
  },
};

export const checkIsRequiredAnswered = ({ questions }) =>
  questions
    .map((question) => {
      const { answerType, isViewOnlyModeEnabled } = question;
      if (isViewOnlyModeEnabled) {
        return true;
      }
      return ANSWER_TYPE_REQUIRED_FILLED_VALIDATOR[answerType]({ ...question });
    })
    .reduce((acc, currVal) => acc && currVal, true);

export const checkRequiredValidationOK = ({ questions, isValidateForm }) => {
  // todo: vikalp only doing for answerType: "DROP_DOWN" (Single Select) for now, later this will have answer type specific validation.
  // also this function should be a common helper for all forms
  if (!isValidateForm) {
    return true;
  }
  return checkIsRequiredAnswered({ questions });
};

const ANSWER_TYPE_FILLED_VALIDATOR = {
  DROP_DOWN: ({ selectedAnswerOptions }) => selectedAnswerOptions?.length === 1,
  MULTI_SELECT: ({
    selectedAnswerOptions,

    minOptions,
    maxOptions,
    answerOptions = [],
  }) => {
    const min = minOptions ?? 0;
    const max = maxOptions ?? answerOptions.length;

    return selectedAnswerOptions?.length >= min && selectedAnswerOptions.length <= max;
  },

  NONE: ({ feedbackText, feedbackTextHtml }) => {
    if (feedbackText || feedbackTextHtml) {
      return true;
    }
    return false;
  },
  STAR_RATING: ({ selectedAnswerOptions }) => selectedAnswerOptions?.length === 1,
  NUMBER_RATING: ({ selectedAnswerOptions }) => selectedAnswerOptions?.length === 1,
};

export const checkIsAnswered = ({ questions }) =>
  questions
    .map((question) => {
      const { answerType } = question;

      return ANSWER_TYPE_FILLED_VALIDATOR[answerType]({ ...question });
    })
    .reduce((acc, currVal) => acc && currVal, true);

export const checkAreAllQuestionsViewOnly = ({ questions }) =>
  questions.every(({ isViewOnlyModeEnabled }) => !!isViewOnlyModeEnabled);

export const checkAreAllQuestionsOptional = ({ questions }) => questions.every(({ isRequired }) => !!isRequired);

export const usePartialPublishEventDetails = () => {
  const { module } = useSidebarNavMatchRoute();
  const page = useSelectedView()?.name;
  const empId = useSelector(selectLoggedInEmployeeId);

  return { module, page, empId };
};

export const isNominated = (review) =>
  ['PEER', 'DLM'].includes(review?.providerType) && review?.peerStatus === 'NOMINATED';

export const getDerivedRowData = ({ reviewStatus }) => ({
  isPendingForAcknowledgement: reviewStatus === REVIEW_FORM_COMPLETION_STATUS.PUBLISHED,
});

export const calcInfoChipTextAndColor = ({ reviewItem, reviewStatus, isMobileView }) => {
  let infoChipColor = 'yellow';
  let infoChipText = '';
  const { review, isRequiredNominationsComplete } = reviewItem;

  const isNominatedReview = isNominated(review);

  const { isPendingForAcknowledgement } = getDerivedRowData({
    reviewStatus,
  });

  if (isNominatedReview) {
    infoChipText = 'Nominated';
    infoChipColor = 'black';
  } else if (isPendingForAcknowledgement) {
    infoChipText = 'Published';
  } else if (review?.providerType === 'NOMINATOR') {
    if (isMobileView) {
      infoChipText += 'Nomination: ';
    }
    infoChipText += isRequiredNominationsComplete ? 'Complete' : 'Pending';

    infoChipColor = isRequiredNominationsComplete ? 'green' : 'red';
  } else {
    infoChipText = REVIEW_SUBMISSION_STATUS_MAP[review.submissionStatus].statusText;
    infoChipColor = REVIEW_SUBMISSION_STATUS_MAP[review.submissionStatus].infoChipColor;
  }
  return { infoChipColor, infoChipText };
};

export const isReviewPublishedV2 = (reviewStatus) =>
  reviewStatus === REVIEW_STATUS_VALUES.PUBLISHED_TO_EMPLOYEE ||
  reviewStatus === REVIEW_STATUS_VALUES.PUBLISHED_TO_MANAGER ||
  reviewStatus === REVIEW_STATUS_VALUES.ACKNOWLEDGED;

export const useGetTimezoneOffset = () => {
  const { organizationTimezone } = useSelector(selectOrgData);
  const foundItem = valuesFromSlack?.find((item) => item.value === organizationTimezone);
  return foundItem ? foundItem.label : null;
};

export const getReviewResultRouteWithSourceAndProviderId = ({
  feedbackFormId,
  employeeId,
  isNewNavEnabled,
  defaultSelectedProviderId,
  source,
  subSource = '',
  intentType,
}) => {
  let route = '';
  const urlSearchParams = new URLSearchParams();

  if (defaultSelectedProviderId) {
    urlSearchParams.set('defaultSelectedProviderId', defaultSelectedProviderId);
  }
  if (source) {
    urlSearchParams.set('source', source);
  }
  if (subSource) {
    urlSearchParams.set('subSource', subSource);
  }
  if (intentType) {
    urlSearchParams.set('intentType', intentType);
  }
  if (isNewNavEnabled) {
    route = `${getDeerReviewResultRoute(feedbackFormId, employeeId)}?${urlSearchParams.toString()}`;
  } else {
    route = `/review/result/${feedbackFormId}/employee/${employeeId}?${urlSearchParams.toString()}`;
  }
  return route;
};
